import { AnimatePresence, motion } from "framer-motion";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  HiOutlineCheckCircle,
  HiOutlinePlus,
  HiOutlineX,
  HiOutlineXCircle,
} from "react-icons/hi";
import {
  MdCheckBox,
  MdCheckBoxOutlineBlank,
  MdOutlineCheckBox,
} from "react-icons/md";
import { useUserData } from "../../../hooks/useUserData";
import { fuzzySearch } from "../utils/fuzzySearch";
import { createPortal } from "react-dom";
import Divider from "../../../components/shared/Divider/Divider";
import { twMerge } from "tailwind-merge";
import Checkbox from "../../../components/shared/Checkbox";
import { useSwingLibrarySettings } from "../hooks/useSwingLibrarySettings";
import {
  useAddTag,
  useUpdateVideoTags,
  useClearVideoTags,
} from "../hooks/useTagMutations";
import { toast } from "react-toastify";

export default function TableRowTag({ className, values, videoId }) {
  const { data: swingLibrarySettings } = useSwingLibrarySettings();

  const { userData } = useUserData();
  const userId = userData?.uid;

  const addTagMutation = useAddTag();
  const updateVideoTagsMutation = useUpdateVideoTags();
  const clearVideoTagsMutation = useClearVideoTags();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const [pos, setPos] = useState({ left: 0, top: 0, width: 0 });

  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const popoverRef = useRef(null);

  const filtered = useMemo(
    () => fuzzySearch(search, swingLibrarySettings?.tags || []),
    [search, swingLibrarySettings?.tags]
  );

  const close = useCallback(() => setOpen(false), []);

  // compute and update popover position
  const updatePosition = useCallback(() => {
    const btn = buttonRef.current;
    if (!btn) return;
    const r = btn.getBoundingClientRect();
    setPos({
      left: r.left,
      top: r.bottom + 8, // 8px gap
      width: Math.max(200, Math.min(320, r.width * 12)), // keep a sensible width
    });
  }, []);

  // open/close + position on open & on resize/scroll
  useEffect(() => {
    if (!open) return;
    updatePosition();

    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open, updatePosition, close]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      const t = e.target;
      if (rootRef.current?.contains(t) || popoverRef.current?.contains(t))
        return;
      close();
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open, close]);

  // Esc to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  const firstTag = values?.[0];
  const hasMore = (values?.length || 0) > 1;

  const handleAddTag = useCallback(async () => {
    const tag = (search || "").trim();
    if (!tag || !userId) return;
    try {
      await addTagMutation.mutateAsync({ tag, userId });

      setSearch("");
    } catch (err) {
      console.error("addTag failed:", err);
    }
  }, [search, userId, addTagMutation]);

  const handleOptionClick = async (tag, type) => {
    try {
      await updateVideoTagsMutation.mutateAsync({ tag, type, videoId });
    } catch (err) {
      console.error("updateVideoTags failed:", err);
    }
  };

  const handleTagsClear = async () => {
    try {
      await clearVideoTagsMutation.mutateAsync({ videoId });
    } catch (err) {
      console.error("clear tags failed:", err);
    }
  };

  const renderOption = (el, isChecked) => (
    <button
      aria-selected={isChecked}
      className={twMerge(
        "flex w-full cursor-pointer items-center gap-2 rounded-sm p-2.5 text-left text-sm",
        "hover:bg-gray100 focus:bg-gray100 focus:outline-none"
      )}
      data-active={isChecked ? "true" : undefined}
      key={String(el)}
      onClick={() => handleOptionClick(el, isChecked ? "remove" : "add")}
      role="option"
      type="button"
    >
      <Checkbox isChecked={isChecked} />

      <span className="truncate">{String(el)}</span>
    </button>
  );

  return (
    <div className={twMerge("relative w-full", className)} ref={rootRef}>
      {/* inline pill(s) + add button */}
      <div className="flex w-full items-center justify-start gap-1">
        {values?.length ? (
          <>
            <span
              className="bg-primary50 text-primary700 truncate rounded-xl px-1.5 py-0.5 text-xs font-semibold"
              title={firstTag}
            >
              {firstTag}
            </span>
            {hasMore ? (
              <span className="bg-gray100 text-gray700 truncate rounded-xl px-1.5 py-0.5 text-xs font-semibold">
                +{values.length - 1}
              </span>
            ) : null}
          </>
        ) : null}

        <button
          aria-controls="tags-popover"
          aria-expanded={open}
          aria-haspopup="dialog"
          className="border-primary700 flex size-5 items-center justify-center rounded-full border"
          onClick={() => setOpen((v) => !v)}
          ref={buttonRef}
          type="button"
        >
          <HiOutlinePlus className="text-primary700 size-3" />
        </button>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              className="z-20 overflow-hidden"
              exit={{ opacity: 0, scale: 0.98, y: -4 }}
              id="tags-popover"
              initial={{ opacity: 0, scale: 0.98, y: -4 }}
              // prevent accidental text selection drags closing, etc.
              onMouseDown={(e) => e.stopPropagation()}
              ref={popoverRef}
              role="dialog"
              // fixed + body coords
              style={{
                left: pos.left,
                minWidth: 200,
                position: "fixed",
                top: pos.top,
                width: pos.width,
              }}
              transition={{ duration: 0.12, ease: "easeOut" }}
            >
              <div className="border-gray200 max-h-96 w-full overflow-y-auto rounded-xl border bg-white shadow-lg">
                <div className="flex items-center justify-between gap-2 px-4 py-3">
                  <div className="text-base font-semibold text-black">Tags</div>
                  {values?.length >= 1 ? (
                    <button
                      className={twMerge(
                        "text-sm font-semibold",
                        values?.length
                          ? "text-error600 hover:underline"
                          : "text-gray400 cursor-default"
                      )}
                      onClick={handleTagsClear}
                      type="button"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>

                <Divider color="gray200" type="horizontal" />

                <div className="p-3">
                  <input
                    autoFocus
                    className={twMerge(
                      "border-gray300 h-9 w-full rounded-md border px-4 text-sm outline-none",
                      "focus:border-primary300 focus:shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05),0px_0px_0px_4px_#F4EBFF]"
                    )}
                    onChange={(e) =>
                      setSearch(e.target.value.replace(/^\s+/, ""))
                    }
                    placeholder="Search"
                    value={search}
                  />
                </div>

                <Divider color="gray200" type="horizontal" />

                <div className="flex flex-col gap-1 px-1.5 py-1">
                  {filtered.map((el) => {
                    const isActive = values?.some(
                      (v) => String(v) === String(el)
                    );
                    return <>{renderOption(el, isActive)}</>;
                  })}
                  {filtered.length === 0 && search.trim() ? (
                    <button
                      className={twMerge(
                        "flex w-full cursor-pointer items-center gap-2 rounded-sm p-2.5 text-left text-sm",
                        "hover:bg-gray100 focus:bg-gray100 focus:outline-none",
                        "justify-center"
                      )}
                      onClick={handleAddTag}
                      type="button"
                    >
                      <HiOutlinePlus className="text-primary700 size-5" />
                      <span className="text-primary700 text-sm">
                        Create “{search.trim()}”
                      </span>
                    </button>
                  ) : null}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
}
